import React from "react";
import { Localizer } from "@/main/react/Types";
import type { OpeningThoughtWithGroupMatchesDTO } from "@/main/react/discussion/OpeningThoughtGroupTypes";

/**
 * What happened to your thought, told to you.
 *
 * <p>This is the half of first read that Reddit's contest mode has no equivalent for, and per the UX
 * design it is the actual point. The protection window fixes a thought's *position*; this fixes the
 * thing the position was a symptom of. Today the reward for writing something is silence, and the
 * reward should be evidence that you were read.
 *
 * <p><b>A3-lite: it reports only what we already count.</b> The design's illustration says "read by 41
 * people in its first hour", which needs a thought-impression counter that does not exist —
 * `thought_views` is genuinely not started, and it is a write-heavy path with its own design. Rather
 * than block the track on it, this reports replies and votes, which is most of the emotional payload
 * and is honest. Re-open the impression counter only if reply rate moves and impressions become the
 * thing worth reporting.
 *
 * <p><b>No numerals, and that is a deliberate localization decision</b> (as-built divergence A-5). A
 * count has to agree with the noun it quantifies, and getting that wrong across 24 locales is a defect
 * class this repo has already shipped in 15 of them: Turkish and Hungarian numerals take the
 * singular, Romanian inserts `de` from 20 up, Slavic needs the genitive at 5+ *and* the nominative
 * plural at 2 to 4. Naming the replier instead of counting the replies sidesteps all of it, reads
 * warmer, and is the sentence the UX design actually wrote ("and @sara replied").
 *
 * <p><b>Additive only.</b> It renders nothing on the server, which is correct rather than a gap: the
 * report is viewer-specific and the thought list is not, and rule 32 constrains JS from *subtracting*
 * from the shipped HTML, never from adding to it. It also renders nothing when there is nothing to
 * report — a line saying that nothing has happened yet is the silence it exists to fix, with extra
 * words.
 *
 * <p>Design: `designs/01-now/2026-08-08-three-participation-mechanics-ux-design.md` §2.2 and the
 * implementation plan's Phase A3.
 */
export function FirstReadAuthorReport({
    openingArgument,
    loggedInUsername,
    localizer,
}: {
    openingArgument: OpeningThoughtWithGroupMatchesDTO;
    loggedInUsername?: string;
    localizer: Localizer;
}): React.JSX.Element | null {
    const thought = openingArgument.openingArgument;
    const authorUsername = thought?.username;

    // Only the author, and only when we actually know who is looking. An anonymous reader has no
    // `loggedInUsername`, and comparing two undefineds would show every visitor a report addressed to
    // somebody else.
    if (!thought || !authorUsername || !loggedInUsername || authorUsername !== loggedInUsername) {
        return null;
    }

    // Reply authors as the server sent them, excluding the author's own replies: answering yourself is
    // not evidence that anyone read you, and reporting it back would be the system flattering them.
    const replierUsernames = Object.values(openingArgument.conversationsMap ?? {})
        .map((reply) => (reply as { username?: string } | undefined)?.username)
        .filter((username): username is string => Boolean(username) && username !== authorUsername);

    // The author's own vote on their own thought is not evidence that anyone read them, and nothing
    // stops them casting one. The stored counters are trigger-maintained totals with no per-voter
    // breakdown, but the card already carries whether THIS viewer voted — and this viewer is the
    // author — so subtracting it is exact rather than approximate.
    const ownVote = thought.isAgreed || thought.isDisagreed ? 1 : 0;
    const voteCount = (thought.agreementsCount ?? 0) + (thought.disagreementsCount ?? 0) - ownVote;

    let message: string | null = null;
    if (replierUsernames.length === 1) {
        message = localizer("thought.firstread.author_report.replied", replierUsernames[0]);
    } else if (replierUsernames.length > 1) {
        message = localizer("thought.firstread.author_report.replied_more", replierUsernames[0]);
    } else if (voteCount > 0) {
        // Votes are the only other evidence of a reader we hold today. Reported without a number for
        // the same reason as above, and because the number is already on the card two lines below —
        // repeating it would be a stat, and this line is a message.
        message = localizer("thought.firstread.author_report.voted");
    }

    if (!message) {
        return null;
    }

    return (
        <p
            className="mb-2 text-xs text-neutral-text"
            data-playwright-id="discussion-thought-first-read-author-report"
        >
            {message}
        </p>
    );
}

export default FirstReadAuthorReport;
